export enum FontFamilyCategory {
  Sans = 'sans',
  Serif = 'serif',
  Mono = 'mono',
  Symbol = 'symbol',
}

enum FontSource {
  Google = 'google',
  WebSafe = 'web-safe',
  Unsafe = 'unsafe',
}

interface FontDefinition {
  name: string;
  source: FontSource;
  category: FontFamilyCategory;
}

const FONT_REGISTRY: readonly FontDefinition[] = [
  // Google: self-loaded by the @import below, so available on every device.
  { name: 'Inter', source: FontSource.Google, category: FontFamilyCategory.Sans },
  { name: 'Montserrat', source: FontSource.Google, category: FontFamilyCategory.Sans },
  { name: 'Open Sans', source: FontSource.Google, category: FontFamilyCategory.Sans },
  { name: 'Oswald', source: FontSource.Google, category: FontFamilyCategory.Sans },
  { name: 'Roboto Condensed', source: FontSource.Google, category: FontFamilyCategory.Sans },
  { name: 'Roboto', source: FontSource.Google, category: FontFamilyCategory.Sans },
  // WebSafe: shipped on Windows + macOS + iOS (iOS is the cut line); stock Android substitutes within the same category.
  { name: 'Arial', source: FontSource.WebSafe, category: FontFamilyCategory.Sans },
  { name: 'Courier New', source: FontSource.WebSafe, category: FontFamilyCategory.Mono },
  { name: 'Georgia', source: FontSource.WebSafe, category: FontFamilyCategory.Serif },
  { name: 'Times New Roman', source: FontSource.WebSafe, category: FontFamilyCategory.Serif },
  { name: 'Trebuchet MS', source: FontSource.WebSafe, category: FontFamilyCategory.Sans },
  { name: 'Verdana', source: FontSource.WebSafe, category: FontFamilyCategory.Sans },
  // Unsafe: missing from at least one of Win/macOS/iOS — usually iOS (CS-849); font-fallback.ts degrades within-category to a loaded webfont + generic family.
  // Desktop Core-Web fonts (Comic Sans / Impact / Arial Black) sit here despite near-universal desktop install because they're absent on iOS.
  // Symbol fonts stay Unsafe regardless of presence — non-standard glyph encoding and no Android equivalent.
  // Avenir Next / Helvetica / Helvetica Neue are Apple-only/licensed, absent on Windows + Android (CORE-6254).
  { name: 'Andale Mono', source: FontSource.Unsafe, category: FontFamilyCategory.Mono },
  { name: 'Arial Black', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Avenir Next', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Book Antiqua', source: FontSource.Unsafe, category: FontFamilyCategory.Serif },
  { name: 'Comic Sans MS', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Helvetica Neue', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Helvetica', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Impact', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Symbol', source: FontSource.Unsafe, category: FontFamilyCategory.Symbol },
  { name: 'Tahoma', source: FontSource.Unsafe, category: FontFamilyCategory.Sans },
  { name: 'Terminal', source: FontSource.Unsafe, category: FontFamilyCategory.Mono },
  { name: 'Webdings', source: FontSource.Unsafe, category: FontFamilyCategory.Symbol },
  { name: 'Wingdings', source: FontSource.Unsafe, category: FontFamilyCategory.Symbol },
];

const GOOGLE_FONT_NAMES = FONT_REGISTRY
  .filter(font => font.source === FontSource.Google)
  .map(font => font.name);

const FONT_FAMILY_CATEGORY_BY_NAME: ReadonlyMap<string, FontFamilyCategory> = new Map(
  FONT_REGISTRY.map(font => [font.name.toLowerCase(), font.category])
);

// Case-insensitive: WYSIWYG content embeds font names in arbitrary case (e.g. `helvetica`, `tahoma`).
export const categoryForFontFamily = (fontName: string): FontFamilyCategory | undefined =>
  FONT_FAMILY_CATEGORY_BY_NAME.get(fontName.trim().toLowerCase());

const generateFontImports = () => {
  return `@import url('https://fonts.googleapis.com/css2?${GOOGLE_FONT_NAMES.map(font => {
    const formattedFont = font.replace(/\s+/g, '+');
    return `family=${formattedFont}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900`;
  }).join('&')}&display=swap')`;
};

export const FONT_IMPORTS = generateFontImports();
export const DEFAULT_FONT_FAMILIES_ARRAY = FONT_REGISTRY.map(font => font.name).sort();

if (typeof window !== 'undefined') {
  (window as any).HALDA_FONT_IMPORTS = FONT_IMPORTS;
}
